<template>
  <div class="px-4 py-3">
    <div v-if="totalCount === 0" class="text-center text-xs text-growth-400 py-6">
      No engagement data available
    </div>
    <template v-else>
      <div class="flex items-baseline justify-between mb-3">
        <div>
          <div class="text-sm font-semibold text-growth-800">Engagement</div>
          <div class="text-[11px] text-growth-500 mt-0.5">Score tier and last-touch recency</div>
        </div>
        <div class="text-[11px] text-growth-500 tabular-nums">
          avg score
          <span class="text-growth-700 font-semibold">{{ averageScore != null ? averageScore.toFixed(1) : '—' }}</span>
          <span class="text-growth-400 ml-2">{{ scoredCount }} of {{ totalCount }} scored</span>
        </div>
      </div>
      <div class="flex gap-6 items-stretch">
        <!-- Tier donut + legend -->
        <div class="flex items-center gap-4 shrink-0 pr-6 border-r border-gray-200">
          <svg :viewBox="`0 0 ${size} ${size}`" :width="msdsize" :height="size" class="block shrink-0">
            <g :transform="`translate(${size/2}, ${size/2})`">
              <template v-if="tierDonutSlices.length > 0">
                <path
                  v-for="(slice, i) in tierDonutSlices"
                  :key="i"
                  :d="slice.path"
                  :fill="slice.color"
                  class="cursor-default transition-opacity duration-150"
                  :opacity="hoveredTier !== null && hoveredTier !== i ? 0.5 : 0.85"
                  @mouseenter="hoveredTier = i"
                  @mouseleave="hoveredTier = null"
                >
                  <title>{{ slice.label }}: {{ slice.count }} ({{ slice.percent }}%)</title>
                </path>
              </template>
              <template v-else>
                <circle :r="(outerRadius + innerRadius) / 2" fill="none" stroke="#e5e7eb" :stroke-width="outerRadius - innerRadius" />
              </template>
            </g>
            <text :x="size/2" :y="size/2 - 6" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-800" style="font-size: 18px; font-weight: 600;">
              {{ averageScore != null ? averageScore.toFixed(1) : '—' }}
            </text>
            <text :x="size/2" :y="size/2 + 10" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-400" style="font-size: 10px;">
              avg score
            </text>
          </svg>
          <div class="flex flex-col gap-1.5">
            <div
              v-for="(slice, i) in tierLegend"
              :key="slice.id"
              class="flex items-center gap-2 cursor-default"
              @mouseenter="hoveredTier = slice.donutIndex"
              @mouseleave="hoveredTier = null"
            >
              <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ backgroundColor: slice.color, opacity: 0.85 }"></span>
              <span class="text-[11px] text-growth-700 whitespace-nowrap">
                {{ slice.label }}
                <span class="text-growth-400 ml-1 tabular-nums">({{ slice.count }})</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Recency bars -->
        <div class="flex-1 flex flex-col gap-2 justify-center min-w-0 py-1">
          <div class="text-[11px] font-semibold text-growth-500 uppercase tracking-wider mb-0.5">
            Last engagement
          </div>
          <div v-for="bucket in recencyBuckets" :key="bucket.id" class="flex items-center gap-3">
            <div class="text-xs font-medium text-growth-800 w-32 truncate shrink-0">
              {{ bucket.label }}
            </div>
            <div class="flex-1 bg-gray-100 rounded h-4 overflow-hidden min-w-0">
              <div
                class="h-4 rounded-l transition-all duration-500 ease-out"
                :style="{ width: Math.max(bucket.percent, 2) + '%', backgroundColor: bucket.color, opacity: 0.75 }"
              />
            </div>
            <div class="text-[11px] text-growth-600 w-28 text-right shrink-0 tabular-nums">
              {{ bucket.percent }}%
              <span class="text-growth-400">({{ bucket.count }}/{{ totalCount }})</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const TIER_DEFS = [
  { id: 't4', label: 'Strong (4.0+)',   color: '#22c55e', min: 4.0, max: Infinity },
  { id: 't3', label: 'Healthy (3–4)',   color: '#eab308', min: 3.0, max: 4.0 },
  { id: 't2', label: 'At risk (2–3)',   color: '#f97316', min: 2.0, max: 3.0 },
  { id: 't1', label: 'Critical (<2)',   color: '#ef4444', min: -Infinity, max: 2.0 },
  { id: 't0', label: 'No score',        color: '#d1d5db', min: null, max: null },
]

const RECENCY_DEFS = [
  { id: 'r30',  label: '0–30 days',   color: '#22c55e', min: 0,   max: 30 },
  { id: 'r90',  label: '31–90 days',  color: '#84cc16', min: 31,  max: 90 },
  { id: 'r180', label: '91–180 days', color: '#eab308', min: 91,  max: 180 },
  { id: 'r181', label: '180+ days',   color: '#f97316', min: 181, max: Infinity },
  { id: 'rno',  label: 'Never',       color: '#d1d5db', min: null, max: null },
]

export default {
  name: 'EntityEngagementCoverage',
  props: {
    allEntities: { type: Array, required: true }
  },
  data() {
    return {
      hoveredTier: null,
      size: 150,
      outerRadius: 68,
      innerRadius: 42
    }
  },
  computed: {
    totalCount() {
      return this.allEntities.length
    },
    scoredCount() {
      return this.allEntities.filter(e => e.engagement_composite != null).length
    },
    averageScore() {
      const scored = this.allEntities.filter(e => e.engagement_composite != null)
      if (scored.length === 0) return null
      const sum = scored.reduce((s, e) => s + Number(e.engagement_composite), 0)
      return sum / scored.length
    },
    tierCounts() {
      const counts = {}
      for (const def of TIER_DEFS) counts[def.id] = 0
      for (const e of this.allEntities) {
        const v = e.engagement_composite
        if (v == null) { counts.t0 += 1; continue }
        const tier = TIER_DEFS.find(d => d.min != null && v >= d.min && v < d.max)
        if (tier) counts[tier.id] += 1
        else counts.t0 += 1
      }
      return counts
    },
    rawTiers() {
      const counts = this.tierCounts
      return TIER_DEFS.map(d => ({
        id: d.id,
        label: d.label,
        color: d.color,
        count: counts[d.id] || 0
      }))
    },
    tierLegend() {
      let donutIdx = 0
      return this.rawTiers.map(t => {
        const inDonut = t.count > 0
        const obj = { ...t, donutIndex: inDonut ? donutIdx : null }
        if (inDonut) donutIdx++
        return obj
      })
    },
    tierDonutSlices() {
      const nonZero = this.rawTiers.filter(t => t.count > 0)
      const total = nonZero.reduce((s, t) => s + t.count, 0)
      if (total === 0) return []
      let cumulative = 0
      return nonZero.map(t => {
        const fraction = t.count / total
        const start = cumulative
        const end = cumulative + fraction
        cumulative = end
        return {
          ...t,
          percent: Math.round((t.count / this.totalCount) * 100),
          path: this.arcPath(start, end)
        }
      })
    },
    recencyBuckets() {
      const counts = {}
      for (const def of RECENCY_DEFS) counts[def.id] = 0
      const now = Date.now()
      for (const e of this.allEntities) {
        const d = e.last_engagement_date
        if (!d) { counts.rno += 1; continue }
        const days = Math.floor((now - new Date(d).getTime()) / 86400000)
        if (isNaN(days)) { counts.rno += 1; continue }
        const bucket = RECENCY_DEFS.find(r => r.min != null && days >= r.min && days <= r.max)
        if (bucket) counts[bucket.id] += 1
        else counts.rno += 1
      }
      const total = this.totalCount
      return RECENCY_DEFS.map(r => ({
        id: r.id,
        label: r.label,
        color: r.color,
        count: counts[r.id] || 0,
        percent: total > 0 ? Math.round(((counts[r.id] || 0) / total) * 100) : 0
      }))
    }
  },
  methods: {
    arcPath(startFraction, endFraction) {
      if (endFraction - startFraction >= 0.9999) {
        return this.fullCirclePath()
      }
      const gap = 0.003
      const s = (startFraction + gap) * 2 * Math.PI - Math.PI / 2
      const e = (endFraction - gap) * 2 * Math.PI - Math.PI / 2
      if (e <= s) return ''
      const largeArc = (endFraction - startFraction) > 0.5 ? 1 : 0
      const oR = this.outerRadius
      const iR = this.innerRadius
      const x1 = oR * Math.cos(s)
      const y1 = oR * Math.sin(s)
      const x2 = oR * Math.cos(e)
      const y2 = oR * Math.sin(e)
      const x3 = iR * Math.cos(e)
      const y3 = iR * Math.sin(e)
      const x4 = iR * Math.cos(s)
      const y4 = iR * Math.sin(s)
      return [
        `M ${x1} ${y1}`,
        `A ${oR} ${oR} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${iR} ${iR} 0 ${largeArc} 0 ${x4} ${y4}`,
        'Z'
      ].join(' ')
    },
    fullCirclePath() {
      const oR = this.outerRadius
      const iR = this.innerRadius
      return [
        `M 0 ${-oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${-oR}`,
        'Z',
        `M 0 ${-iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${-iR}`,
        'Z'
      ].join(' ')
    }
  }
}
</script>
